import React, { useEffect, useState } from "react";
import AdminShell from "@/pages/admin/AdminShell";
import { Section } from "@/pages/Shell";
import { UsersAdmin } from "@/pages/admin/AdminDashboard";
import { api, formatError } from "@/lib/api";
import { UserPlus } from "@phosphor-icons/react";

/**
 * /users — Admin users management.
 *
 * The "Invitar usuario" flow now uses the secure invitation-link pattern
 * (PR #4) for the investor role:
 *   1. POST /auth/register creates the user with a random server-side password
 *      that is NEVER shown or stored in plain text. (Required by the existing
 *      backend contract.)
 *   2. POST /admin/investors/:id/send-invitation generates a one-time
 *      activation token and emails the investor an "Activar mi cuenta" link.
 *   3. Investor clicks the link → /activar → sets their own password →
 *      auto-logs in.
 *
 * For non-investor roles (constructor, marketing, admin) we keep the legacy
 * temporary-password flow for now (no behavioral regression).
 */
export default function UsersPage() {
  const [msg, setMsg] = useState("");
  const [msgTone, setMsgTone] = useState("info"); // info | success | error
  const [showInvite, setShowInvite] = useState(false);
  const [invite, setInvite] = useState({ email: "", name: "", role: "investor" });
  const [busy, setBusy] = useState(false);

  const sendInvite = async (e) => {
    e.preventDefault();
    setBusy(true); setMsg(""); setMsgTone("info");
    const isInvestor = invite.role === "investor";
    // Random password — never displayed nor stored anywhere outside Mongo.
    // For investors it's immediately replaced when they accept the invitation.
    // For other roles, an admin will set/reset their password manually for V1.
    const tempPassword = `Tmp${Math.random().toString(36).slice(2, 12)}!Z`;
    try {
      const { data: created } = await api.post("/auth/register", {
        email: invite.email.toLowerCase().trim(),
        password: tempPassword,
        name: invite.name.trim(),
        role: invite.role,
      });
      if (isInvestor) {
        try {
          const { data: inv } = await api.post(
            `/admin/investors/${created.user_id}/send-invitation`,
            {},
          );
          if (inv.email_status === "ok") {
            setMsg(`✓ Invitación enviada a ${invite.email}.`);
            setMsgTone("success");
          } else if (inv.email_status === "skipped") {
            setMsg(`Usuario creado. Email deshabilitado por configuración — pulsa “Reenviar invitación” cuando esté activo.`);
            setMsgTone("info");
          } else {
            setMsg(`Usuario creado, pero el email no se envió. Pulsa “Reenviar invitación” en la lista para reintentar.`);
            setMsgTone("error");
          }
        } catch (err) {
          setMsg(`Usuario creado, pero no se pudo enviar la invitación: ${formatError(err)}`);
          setMsgTone("error");
        }
      } else {
        // Non-investor: keep the old fallback messaging. Admin shares the
        // temp password securely out of band.
        setMsg(`✓ Usuario ${invite.role} creado. Contraseña temporal: ${tempPassword} — compártela de forma segura.`);
        setMsgTone("info");
      }
      setInvite({ email: "", name: "", role: "investor" });
      setShowInvite(false);
      window.dispatchEvent(new Event("users:reload"));
    } catch (err) {
      setMsg(formatError(err));
      setMsgTone("error");
    } finally {
      setBusy(false);
    }
  };

  const msgClass = {
    success: "bg-emerald-50 border-emerald-200 text-emerald-800",
    error:   "bg-red-50 border-red-200 text-red-800",
    info:    "bg-stone-50 border-stone-200 text-stone-700",
  }[msgTone];

  return (
    <AdminShell>
      <div className="flex items-end justify-between mb-5 gap-4 flex-wrap">
        <div>
          <h2 className="font-serif-dv text-3xl leading-tight">Usuarios</h2>
          <p className="text-stone-500 mt-1">Gestiona los miembros del equipo y asigna roles.</p>
        </div>
        <button
          onClick={() => setShowInvite(!showInvite)}
          className="dv-btn dv-btn-primary"
          data-testid="user-invite-btn"
        >
          <UserPlus size={16} /> Invitar usuario
        </button>
      </div>

      {showInvite && (
        <form onSubmit={sendInvite} className="dv-card mb-6 grid sm:grid-cols-2 gap-3" data-testid="user-invite-form">
          <input
            className="dv-input" type="email" placeholder="email@…" required
            value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })}
            data-testid="invite-email"
          />
          <input
            className="dv-input" type="text" placeholder="Nombre" required
            value={invite.name} onChange={(e) => setInvite({ ...invite, name: e.target.value })}
            data-testid="invite-name"
          />
          <select
            className="dv-input" value={invite.role}
            onChange={(e) => setInvite({ ...invite, role: e.target.value })}
            data-testid="invite-role"
          >
            <option value="investor">Inversor</option>
            <option value="constructor">Constructor</option>
            <option value="marketing">Marketing</option>
            <option value="admin">Admin</option>
          </select>
          <div className="sm:col-span-2 text-xs text-stone-500">
            {invite.role === "investor"
              ? "Se enviará un email de “Activar mi cuenta” al inversor. El enlace expira automáticamente."
              : "Se generará una contraseña temporal que deberás compartir de forma segura."}
          </div>
          <div className="sm:col-span-2 flex gap-3">
            <button disabled={busy} className="dv-btn dv-btn-primary flex-1" data-testid="invite-submit">
              {busy ? "Creando…" : (invite.role === "investor" ? "Crear y enviar invitación" : "Crear cuenta")}
            </button>
            <button type="button" onClick={() => setShowInvite(false)} className="dv-btn dv-btn-ghost">Cancelar</button>
          </div>
        </form>
      )}

      {msg && (
        <div className={`mb-5 text-sm px-3 py-2 rounded-md border ${msgClass}`} data-testid="user-msg">
          {msg}
        </div>
      )}

      {/* Reuse existing UsersAdmin for list + role change */}
      <UsersListReloadable />

      {/* PR #4 — admin tool to (re)send invitations + view status. */}
      <Section title="Invitaciones de inversores" subtitle="Estado y reenvío de invitaciones de activación.">
        <InvestorInvitationsAdmin />
      </Section>
    </AdminShell>
  );
}

/** Thin wrapper that re-renders UsersAdmin on "users:reload" events. */
function UsersListReloadable() {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const h = () => setTick((t) => t + 1);
    window.addEventListener("users:reload", h);
    return () => window.removeEventListener("users:reload", h);
  }, []);
  return <UsersAdmin key={tick} />;
}

/**
 * InvestorInvitationsAdmin — list of investor users with their derived
 * invitation status + a "(Re)enviar invitación" button.
 *
 * Status comes from GET /admin/investors/:id/invitation-status, derived
 * server-side from the latest token row + the user's activated_at.
 */
function InvestorInvitationsAdmin() {
  const [investors, setInvestors] = useState([]);
  const [statusByUser, setStatusByUser] = useState({}); // user_id -> {status, latest_token_expires_at, ...}
  const [busyId, setBusyId] = useState(null);
  const [feedback, setFeedback] = useState(null); // {user_id, tone, text}
  const [loading, setLoading] = useState(true);

  const loadAll = async () => {
    setLoading(true);
    try {
      const { data: users } = await api.get("/users");
      const inv = (users || []).filter(u => u.role === "investor");
      setInvestors(inv);
      const map = {};
      await Promise.all(inv.map(async (u) => {
        try {
          const { data } = await api.get(`/admin/investors/${u.user_id}/invitation-status`);
          map[u.user_id] = data;
        } catch {
          map[u.user_id] = { status: "pending" };
        }
      }));
      setStatusByUser(map);
    } catch {
      setInvestors([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    const h = () => loadAll();
    window.addEventListener("users:reload", h);
    return () => window.removeEventListener("users:reload", h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sendInvitation = async (u) => {
    setBusyId(u.user_id);
    setFeedback(null);
    try {
      const { data } = await api.post(`/admin/investors/${u.user_id}/send-invitation`, {});
      if (data.email_status === "ok") {
        setFeedback({ user_id: u.user_id, tone: "success", text: "Invitación enviada." });
      } else if (data.email_status === "skipped") {
        setFeedback({ user_id: u.user_id, tone: "info", text: "Email deshabilitado por configuración." });
      } else {
        setFeedback({ user_id: u.user_id, tone: "error", text: "Invitación creada, email no enviado. Pulsa “Reenviar invitación” para reintentar." });
      }
      await loadAll();
    } catch (e) {
      setFeedback({ user_id: u.user_id, tone: "error", text: formatError(e) });
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return <div className="dv-card text-center py-6 text-stone-500 text-sm">Cargando…</div>;
  }
  if (investors.length === 0) {
    return <div className="dv-card text-center py-6 text-stone-500 text-sm">No hay inversores registrados.</div>;
  }

  return (
    <div className="dv-card p-0 overflow-hidden">
      <div className="overflow-x-auto">
      <table className="w-full text-sm min-w-[640px]">
        <thead className="bg-stone-50 border-b border-stone-200 text-left">
          <tr className="text-xs tracking-wider uppercase text-stone-500">
            <th className="px-4 py-3">Inversor</th>
            <th className="px-4 py-3">Email</th>
            <th className="px-4 py-3">Estado</th>
            <th className="px-4 py-3">Expira</th>
            <th className="px-4 py-3 text-right">Acción</th>
          </tr>
        </thead>
        <tbody>
          {investors.map(u => {
            const s = statusByUser[u.user_id] || { status: "pending" };
            const isAccepted = s.status === "accepted";
            const busy = busyId === u.user_id;
            const fb = feedback && feedback.user_id === u.user_id ? feedback : null;
            return (
              <tr key={u.user_id} className="border-b border-stone-100" data-testid={`invitation-row-${u.user_id}`}>
                <td className="px-4 py-3 font-medium">{u.name || <span className="italic text-stone-400">(sin nombre)</span>}</td>
                <td className="px-4 py-3 text-stone-600">{u.email}</td>
                <td className="px-4 py-3">
                  <InvitationStatusBadge status={s.status} />
                </td>
                <td className="px-4 py-3 text-stone-500 text-xs">
                  {s.latest_token_expires_at
                    ? new Date(s.latest_token_expires_at).toLocaleDateString("es-ES", { day: "numeric", month: "short", year: "numeric" })
                    : "—"}
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={() => sendInvitation(u)}
                    disabled={busy}
                    data-testid={`invitation-send-${u.user_id}`}
                    className="text-xs px-3 py-1 rounded border border-stone-300 text-stone-700 hover:bg-stone-900 hover:text-white hover:border-stone-900 transition-all disabled:opacity-50"
                  >
                    {busy ? "…" : (isAccepted
                      ? "Reenviar invitación"
                      : (s.status === "email_failed"
                        ? "Reintentar envío"
                        : (s.status === "sent" || s.status === "email_disabled" || s.status === "expired"
                          ? "Reenviar invitación"
                          : "Enviar invitación")))}
                  </button>
                  {fb && (
                    <div className={`mt-2 text-xs ${fb.tone === "success" ? "text-emerald-700" : fb.tone === "error" ? "text-red-700" : "text-stone-600"}`}>
                      {fb.text}
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
    </div>
  );
}

function InvitationStatusBadge({ status }) {
  const map = {
    pending:        { label: "Invitación pendiente",            cls: "bg-stone-100 text-stone-700 border-stone-200" },
    sent:           { label: "Invitación enviada",              cls: "bg-amber-50 text-amber-800 border-amber-200" },
    email_failed:   { label: "Email fallido — Reintentar",      cls: "bg-red-50 text-red-700 border-red-200" },
    email_disabled: { label: "Email deshabilitado por configuración", cls: "bg-stone-100 text-stone-600 border-stone-200" },
    accepted:       { label: "Cuenta activada",                 cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    expired:        { label: "Invitación expirada",             cls: "bg-red-50 text-red-700 border-red-200" },
  };
  const meta = map[status] || map.pending;
  return (
    <span
      className={`inline-flex items-center text-[11px] px-2 py-0.5 rounded-full border ${meta.cls}`}
      data-testid={`invitation-status-${status}`}
    >
      {meta.label}
    </span>
  );
}
